import { useRef, useState } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_URL = "https://readdy.ai/api/form/daetjaptto0ceqqnh3n0";

type Status = "idle" | "loading" | "success" | "error";

const typeOptions = ["话题线索", "深度建议", "网站反馈", "合作联系"];

export default function ReportSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [len, setLen] = useState(0);

  const go = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐：若被自动填充则静默当作成功处理，不发送
    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot !== "") {
      setStatus("success");
      setSuccessMsg("提交成功，感谢你的参与！");
      return;
    }

    setStatus("loading");
    setFormError("");
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "website_alt" && String(value).trim() !== "") payload.append(key, String(value));
    });

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, any> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const ok = res.ok && code === "OK";
      if (!ok || String(serverMsg).toLowerCase().includes("spam")) {
        setStatus("error");
        setFormError(String(serverMsg && serverMsg.trim() ? serverMsg : "提交失败，请稍后再试。"));
        return;
      }
      setStatus("success");
      setSuccessMsg("提交成功，感谢你的参与！我们会在审核后与你联系。");
      form.reset();
      setLen(0);
    } catch {
      setStatus("error");
      setFormError("网络开小差了，请稍后重试。");
    }
  };

  return (
    <section id="report" className="scroll-mt-24 bg-accent-950">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* 左：投稿说明 */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-upload-cloud-2-line"></i>
              投稿爆料
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl">
              有线索想告诉我们？
              <br />
              一起把下一个热点讲清楚
            </h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-accent-200">
              无论你发现了一个值得关注的话题、想给我们栏目提建议，还是有内容合作意向，都欢迎留下信息。我们会在审核后尽快回复你。
            </p>

            <ul className="mt-7 space-y-3.5 text-sm text-accent-100">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50"><i className="ri-shield-check-line"></i></span>
                <span>请提供真实、可查证的信息，避免主观臆测与未证实内容。</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50"><i className="ri-lock-line"></i></span>
                <span>尊重他人隐私与权益，投稿时请勿包含未经许可的个人信息。</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50"><i className="ri-mail-open-line"></i></span>
                <span>商务与合作，可来信：<span className="text-background-50">hello@chiagua17.readdy.site</span></span>
              </li>
            </ul>
          </div>

          {/* 右：表单 */}
          <div className="rounded-2xl bg-background-50 p-6 shadow-none md:p-8">
            <h3 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-edit-line text-primary-600"></i>
              填写投稿信息
            </h3>

            {status === "success" && (
              <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-accent-100 p-4 text-sm text-accent-900">
                <i className="ri-checkbox-circle-fill mt-0.5"></i>
                <span>{successMsg}</span>
              </div>
            )}
            {status === "error" && (
              <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-primary-100 p-4 text-sm text-primary-900">
                <i className="ri-error-warning-fill mt-0.5"></i>
                <span>{formError}</span>
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form className="report-form mt-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-bold text-foreground-800">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-bold text-foreground-800">联系邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="方便我们回复你"
                    className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="type" className="mb-1.5 block text-sm font-bold text-foreground-800">投稿类型</label>
                <div className="flex flex-wrap gap-2">
                  {typeOptions.map((opt) => (
                    <label key={opt} className="cursor-pointer">
                      <input type="radio" name="type" value={opt} defaultChecked={opt === "话题线索"} className="peer sr-only" />
                      <span className="inline-flex cursor-pointer items-center rounded-full border border-background-300 bg-background-100 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors peer-checked:border-accent-500 peer-checked:bg-accent-500 peer-checked:text-background-50">
                        {opt}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="topic" className="mb-1.5 block text-sm font-bold text-foreground-800">事件或话题</label>
                <input
                  id="topic"
                  name="topic"
                  type="text"
                  required
                  maxLength={60}
                  placeholder="用一句话概括你看到的热点"
                  className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 flex items-center justify-between text-sm font-bold text-foreground-800">
                  详细说明
                  <span className="font-normal text-foreground-500">{len} / 500</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setLen(e.target.value.length)}
                  placeholder="请尽量补充事件背景、时间与可查证来源线索"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              {/* 蜜罐字段（对真实用户隐藏） */}
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="trap-input" />

              <button
                type="submit"
                disabled={status === "loading"}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    提交投稿
                  </>
                )}
              </button>
              <p className="text-center text-xs text-foreground-600">
                提交即表示你同意我们依据内容原则进行审核与收录
              </p>
            </form>

            <div className="mt-6 border-t border-background-200 pt-5">
              <p className="text-sm font-bold text-foreground-800">没话题想聊？不如先逛逛</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => go("rank")} className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-3.5 py-2 text-sm font-bold whitespace-nowrap text-secondary-900 transition-colors hover:bg-secondary-200">
                  <i className="ri-fire-line"></i>热榜排行
                </button>
                <button type="button" onClick={() => go("focus")} className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-3.5 py-2 text-sm font-bold whitespace-nowrap text-secondary-900 transition-colors hover:bg-secondary-200">
                  <i className="ri-folder-2-line"></i>焦点专区
                </button>
                <button type="button" onClick={() => go("deep")} className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-3.5 py-2 text-sm font-bold whitespace-nowrap text-secondary-900 transition-colors hover:bg-secondary-200">
                  <i className="ri-book-open-line"></i>深度讨论
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}